import { db } from "./DBHelper";
import { COLLECTIONS, STORAGE_KEYS } from "../const";
import SalesPersonService from "services/SalespersonService";

const SALESPERSONS_CACHE_DURATION_IN_MS = 24 * 60 * 60 * 1000;

export const saveSalespersons = async (list) => {
  const existingSalespersons = await getSalespersons();
  const existingMap = new Map((existingSalespersons || []).map((sp) => [sp.id, sp]));

  const mergedList = (list || []).map((newItem) => {
    const existing = existingMap.get(newItem.id);
    if (existing) {
      return {
        ...newItem,
        authToken: newItem.authToken ?? existing.authToken,
      };
    }
    return newItem;
  });

  await db.addAll(COLLECTIONS.SALESPERSONS.name, mergedList);
  return mergedList;
};

export const getSalespersons = () => {
  return db.getAll(COLLECTIONS.SALESPERSONS.name);
};

export const getSalespersonByUsername = async (username) => {
  const salesPersons = await db.filter(COLLECTIONS.SALESPERSONS.name, { username });
  return salesPersons[0] || null;
};

export const getSalespersonByAuthToken = async (authToken) => {
  const salesPersons = await db.filter(COLLECTIONS.SALESPERSONS.name, { authToken });
  return salesPersons[0] || null;
};

export const getSalespersonById = async (id) => {
  return db.get(COLLECTIONS.SALESPERSONS.name, id);
};

export const updateSalesPersonById = async (id, token) => {
  const salesPersons = await db.getAll(COLLECTIONS.SALESPERSONS.name);
  const existing = salesPersons.find((sp) => sp.id === id);
  if (!existing) return null;

  const updatedSalesPerson = {
    ...existing,
    authToken: token,
  };

  await db.update(COLLECTIONS.SALESPERSONS.name, id, updatedSalesPerson);

  return updatedSalesPerson;
};

export const loadInitialSalespersons = async (syncFromBE = false) => {
  const now = Date.now();
  const lastSyncedAt = Number(localStorage.getItem(STORAGE_KEYS.LAST_SALESPERSON_FETCH_TIME));

  const cacheExpired = !lastSyncedAt || now - lastSyncedAt > SALESPERSONS_CACHE_DURATION_IN_MS;

  if (!cacheExpired && !syncFromBE) {
    const cached = await getSalespersons();
    if (cached.length) {
      return {
        salespersons: cached,
        fromCache: true,
      };
    }
  }

  try {
    const response = await SalesPersonService.getSalesPersons();
    const salespersons = Array.isArray(response) ? response : response?.results || [];

    if (!Array.isArray(salespersons)) {
      throw new Error("Invalid format for salesperson data");
    }

    const savedList = await saveSalespersons(salespersons);

    localStorage.setItem(STORAGE_KEYS.LAST_SALESPERSON_FETCH_TIME, String(now));

    return {
      salespersons: savedList,
      fromCache: false,
    };
  } catch (error) {
    const cached = await getSalespersons();
    if (cached.length) {
      return {
        salespersons: cached,
        fromCache: true,
      };
    }
    throw error;
  }
};

export const setCurrentSalesperson = (salesperson) => {
  if (!salesperson) {
    clearSalespersonSession();
    return;
  }
  localStorage.setItem(STORAGE_KEYS.SALESPERSON, JSON.stringify(salesperson));
  localStorage.setItem(STORAGE_KEYS.SALESPERSON_LOGGED_IN_TIME, String(Date.now()));
};

export const getCurrentSalesperson = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SALESPERSON);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.error("Error reading selected salesperson from localStorage:", e);
    return null;
  }
};

export const clearSalespersonSession = () => {
  localStorage.removeItem(STORAGE_KEYS.SALESPERSON);
  localStorage.removeItem(STORAGE_KEYS.SALESPERSON_LOGGED_IN_TIME);
};

export const isSalespersonLoginRequired = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.TERMINAL_SETTINGS);
    if (cached) {
      const parsed = JSON.parse(cached);
      return parsed?.salespersonLogin ?? true;
    }
  } catch (e) {
    console.error("Error checking salesperson login setting:", e);
  }
  return true;
};

export const findSalespersonByPassword = async (password) => {
  const list = await getSalespersons();
  return list.find((p) => String(p.password) === String(password)) || null;
};
